import * as React from 'react';
import { Col, Layout, Menu, Row, Typography } from 'antd';
import { NavLink } from 'react-router-dom';
import { default as SiteMenu, linkTypes, SiteMenuProps } from './SiteMenu';
import { GlobalContext } from '../../Contexts/GlobalContext';
import { Contract, User } from 'tree-wallet-client';
import MenuContractSelector from './MenuContractSelector';
import { DESKTOP_MENU_COLLAPSED_KEY } from '../../Service/AuthService';
import { BUILD_STAMP } from '../../Service/ConfigurationService';


const { Sider } = Layout;
const { Title } = Typography;

// Copied to dist/icons by CopyPlugin: importing it here would emit a second, identical 58 KB file.
const logo = '/dist/icons/usf-logo-blue.png';

// The desktop menu's width, fixed: antd's own default, written out so the logo below is sized from it
// (100% of the Sider) - a wide logo image must never be what decides the sidebar's width
const SITE_MENU_WIDTH = 200;

export default class DesktopMenu extends SiteMenu {
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>;

  constructor(props: SiteMenuProps) {
    super(props);
    this.state = { ...this.state, opened: localStorage.getItem(DESKTOP_MENU_COLLAPSED_KEY) === '1' };
  }

  componentDidUpdate(_prevProps: SiteMenuProps, prevState: { opened: boolean }) {
    if (prevState.opened !== this.state.opened) {
      localStorage.setItem(DESKTOP_MENU_COLLAPSED_KEY, this.state.opened ? '1' : '0');
    }
  }

  // antd also calls this on mount, so it sets the state instead of flipping it - a toggle would undo the
  // state just restored from localStorage
  private onCollapseChange = (collapsed: boolean) => this.setState({ opened: collapsed });

  render() {
    const { opened } = this.state;
    const links = this.getLinks();
    // The entry of the page being shown is highlighted in Left-Menu
    const pathname = this.context.location?.pathname || window.location.pathname;
    const selectedKey = `2${pathname === '/' ? '/admin' : pathname}`;

    return (
      <GlobalContext.Consumer>
        {({ user, contracts, contract }) => {
          return <Sider theme='light' className={'menu desktop' + (user ? '' : ' public')} width={SITE_MENU_WIDTH} collapsible collapsed={opened} onCollapse={this.onCollapseChange}>
            {/* The 10px of padding is the menu's own blue left showing on each side of the logo (see the less) */}
            <div className='menu-logo'>
              {/* Width forced in `sitemenu.less`, and capped at the menu's own width (`SITE_MENU_WIDTH`) */}
              <img src={logo} style={{ width: '100%', maxWidth: `${SITE_MENU_WIDTH}px`, height: 'auto' }} />
            </div>
            {contracts && contracts.length > 0 &&
              <Row style={{ textAlign: 'center', margin: '20px 0', visibility: 'hidden' }}>
                <Col span={24}><Title level={5} style={{ color: 'white' }}>Current contract</Title></Col>
                <Col span={24}><MenuContractSelector /></Col>
              </Row>
            }
            {/* `items` instead of `Menu.Item` children: antd 4.24 warns that `children` goes away in the
                next major. Links that must not show come back as null from `renderLink`, hence the filter. */}
            {/* `selectedKeys` from the route: nothing is kept from clicks, so the action item (Download KML)
                no longer keeps antd's 3px selection bar after being used */}
            <Menu
              theme="light"
              selectedKeys={[selectedKey]}
              mode="inline"
              onClick={this.onMenuClick}
              items={links.map((link: any) => this.renderLink(user, contract, link)).filter(Boolean)}
            />
            <div id={'desktop-version-number'}>
              <div>TreeWallet Build<br />{BUILD_STAMP}</div>
            </div>
          </Sider>
        }}
      </GlobalContext.Consumer>
    );
  }

  // Collapsed menu: antd hides the item's label, so the tap lands on the item itself. Button-type links
  // (Download KML) are therefore triggered here, not from the label - which also avoids a double call
  // when the label is visible
  private onMenuClick = ({ key }: { key: string }) => {
    const link = this.getLinks().find((item: any) => `2${item.link}` === key && item.type === linkTypes.btn);
    link?.onClick?.();
  }

  renderLink(user: User | undefined, contract: Contract | undefined, link: any) {
    const { navigate } = this.context;

    if (!this.shouldDisplayLink(user, contract, link)) {
      return null;
    }

    const linkProps = { style: { color: '#fff' }, className: 'menu-link' };

    const template = (link: any) => {
      switch (link.type) {
        case linkTypes.logout:
          return <NavLink{...linkProps} to={'/'} onClick={() => link.onClick(navigate)}>{link.text}</NavLink>;
        case linkTypes.btn:
          return <span {...linkProps}>{link.text}</span>;
        default:
          return <NavLink {...linkProps} to={link.link}
            onClick={(event) => this.followMenuLink(event, navigate, link.link)}>{link.text}</NavLink>;
      }
    }
    return {
      key: "2" + link.link,
      style: { backgroundColor: link.type === linkTypes.btn ? "inherit" : "" },
      icon: link.icon,
      label: link && template(link),
    };
  }
}